import React from "react";
import { Trans } from "react-i18next/icu.macro";

import {
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import useI18nNamespace from "@services/useI18nNamespace";

import type { FetchErrorModalBodyProps } from "./types";

/**
 * Error-specific custom modal body to inform the user of what error has occurred.
 */
export const FetchErrorModalBody = ({ error }: FetchErrorModalBodyProps): React.ReactElement => {
  const { t, k } = useI18nNamespace("server-fetch-error-messages");

  if (error instanceof FetchServerError) {
    switch (error.status) {
      case 503:
      case 504:
        return (
          <Trans i18nKey={k("request-timeout-body")}>
            Your request timed out. Please try refreshing the page. If you continue to receive this error contact{" "}
            <a href="https://www.braze.com/docs/support_contact">support</a> directly.
          </Trans>
        );
      case 500:
        return (
          <>
            {error.message === "An unexpected error has occurred"
              ? t("generic-unexpected-error-body", "An unexpected error has occurred")
              : error.message}
          </>
        );
    }
  }
  if (error instanceof FetchElevatedAccessError) {
    return (
      <Trans i18nKey={k("fetch-elevated-access-error-body")}>
        This page has heightened security requirements and reauthentication is required. Please refresh your browser
        and try again.
      </Trans>
    );
  }
  if (error instanceof FetchNotFoundError) {
    return (
      <Trans i18nKey={k("fetch-not-found-body")}>
        Oops! It looks like we made a mistake. The page you requested could not be found.
        <br />
        <br />
        If you need further assistance, contact <a href="https://www.braze.com/docs/support_contact">support</a>
        directly. When contacting support, please include any details you can to help reproduce the error.
      </Trans>
    );
  }
  if (error instanceof FetchUnauthorizedError) {
    return (
      <Trans i18nKey={k("unauthorized-body")}>
        Oops! You don&apos;t have the permissions for the requested action.
        <br />
        <br />
        Contact your company&apos;s administrator to request access or change your permissions.
      </Trans>
    );
  }
  if (error instanceof FetchPasswordInvalidError) {
    return (
      <Trans i18nKey={k("password-expired-or-invalid-body")}>
        Your administrator has updated your password requirements, or your password has expired.
        <br />
        <br />
        Please refresh the page to update your password.
      </Trans>
    );
  }
  if (error instanceof FetchSsoError) {
    return (
      <Trans i18nKey={k("sso-body")}>
        Your administrator now requires SSO authentication.
        <br />
        <br />
        Please logout then login using a Single Sign-On provider.
      </Trans>
    );
  }
  if (error.status === UNIDENTIFIED_ERROR) {
    return (
      <Trans i18nKey={k("unidentified-error-body")}>
        Oops! An unspecified error has occurred. Refresh your page and everything should be fine! If you keep seeing
        this message, please reach out to support.
      </Trans>
    );
  }
  // regular errors just diplsay their message, unformatted
  return <>{error.message}</>;
};
